/**
 * Lighthouse Controls — policies, egress, cost, MCP servers.
 */
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useLighthouseMcpServers } from "@/hooks/security/useLighthouseData";

export default function LhControlsPage() {
  return (
    <ConnectorGate connectorId="lighthouse">
      <Inner />
    </ConnectorGate>
  );
}

function Inner() {
  const mcpQ = useLighthouseMcpServers();
  const mcp = mcpQ.data ?? [];
  const sanctioned = mcp.filter((m) => m.sanctioned).length;

  return (
    <SurfaceShell
      overline="AI Governance · Controls"
      title="What governs agent behavior"
      summary="Policies, MCP endpoints, egress and cost guardrails. Configure once, enforced everywhere."
      kpis={
        <>
          <StatTile label="Policies active" value={0} hint="Connect Lighthouse to populate" />
          <StatTile label="MCP servers" value={mcp.length} loading={mcpQ.isLoading || mcpQ.isError} hint={`${sanctioned} sanctioned`} />
          <StatTile label="Egress events 24h" value={0} hint="Coming online" />
          <StatTile label="Cost · 30d" value="—" hint="Coming online" />
        </>
      }
    >
      <Tabs defaultValue="mcp">
        <TabsList>
          <TabsTrigger value="mcp">MCP servers</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="egress">Egress</TabsTrigger>
          <TabsTrigger value="cost">Cost</TabsTrigger>
        </TabsList>

        <TabsContent value="mcp" className="mt-4">
          <McpTab rows={mcp} loading={mcpQ.isLoading} />
        </TabsContent>
        <TabsContent value="policies" className="mt-4">
          <SoonPanel
            title="Policies"
            description="Define what agents can do — tool calls, scopes, approval requirements — as versioned YAML."
          />
        </TabsContent>
        <TabsContent value="egress" className="mt-4">
          <SoonPanel
            title="Egress"
            description="Monitor every outbound network call agents make. Block unsanctioned destinations before data leaves."
          />
        </TabsContent>
        <TabsContent value="cost" className="mt-4">
          <SoonPanel
            title="Cost attribution"
            description="Per-agent and per-team token consumption with budget alerts."
          />
        </TabsContent>
      </Tabs>
    </SurfaceShell>
  );
}

function McpTab({ rows, loading }: { rows: any[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  return (
    <Panel title={`Discovered MCP servers · ${rows.length}`}>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No MCP servers discovered.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {["Name","URL","Transport","Publisher","Tools","Sanctioned"].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id ?? r.server_id ?? i} className="border-b border-border/60 hover:bg-muted/40">
                  <td className="px-3 py-2 text-foreground font-medium">{r.name ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-muted-foreground truncate max-w-[280px]">{r.url ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.transport ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.publisher ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums text-foreground">{r.tool_count ?? 0}</td>
                  <td className="px-3 py-2">
                    {r.sanctioned ? (
                      <span className="text-success text-[11px]">✓ yes</span>
                    ) : (
                      <span className="text-warning text-[11px]">no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function SoonPanel({ title, description }: { title: string; description: string }) {
  return (
    <Panel title={title}>
      <div className="py-8 text-center max-w-md mx-auto">
        <p className="text-sm text-foreground">{description}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          This control will populate as soon as the Lighthouse policy engine starts streaming events for your tenant.
        </p>
      </div>
    </Panel>
  );
}
